import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  advanceTransferStatus,
  isTransferActive,
  isTransferInFlight,
  type PrivateTransfer,
  type PrivateTransferStatus,
  type PrivateTransferToken,
} from './private-transfer.types';

interface PrivateTransferState {
  tokens: PrivateTransferToken[];
  tokensLoaded: boolean;
  tokensErrorKey: string | null;
  /** One list slot for the whole slice: the latest page the history (or the send form) asked for. */
  transfers: PrivateTransfer[];
  transfersCount: number;
  transfersLoading: boolean;
  /** Why the last list load failed; an empty list must never stand in for "could not load". */
  transfersErrorKey: string | null;
  /** Transfer the progress screen is watching; polled while in flight. */
  trackedTransferId: string | null;
  trackedErrorKey: string | null;
}

const initialState: PrivateTransferState = {
  tokens: [],
  tokensLoaded: false,
  tokensErrorKey: null,
  transfers: [],
  transfersCount: 0,
  transfersLoading: false,
  transfersErrorKey: null,
  trackedTransferId: null,
  trackedErrorKey: null,
};

/**
 * Root so the rows survive moving between the send form, the progress screen
 * and the history; registered in `RESETTABLE_STORES` so logout wipes them.
 * Polling and sockets live in the route-scoped `PrivateTransferSyncService`.
 */
export const PrivateTransferStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    trackedTransfer: computed(
      () => store.transfers().find(transfer => transfer.id === store.trackedTransferId()) ?? null,
    ),
    hasInFlight: computed(() =>
      store.transfers().some(transfer => isTransferInFlight(transfer.status)),
    ),
    /** Rows the backend counts toward its cap of active transfers (manual review included). */
    activeCount: computed(
      () => store.transfers().filter(transfer => isTransferActive(transfer.status)).length,
    ),
  })),
  withMethods(store => ({
    setTokens(tokens: PrivateTransferToken[]): void {
      patchState(store, { tokens, tokensLoaded: true, tokensErrorKey: null });
    },
    setTokensError(tokensErrorKey: string): void {
      patchState(store, { tokensErrorKey, tokensLoaded: true });
    },
    setTransfersLoading(transfersLoading: boolean): void {
      patchState(store, { transfersLoading });
    },
    setTransfers(transfers: PrivateTransfer[], count = transfers.length): void {
      patchState(store, {
        transfers,
        transfersCount: count,
        transfersLoading: false,
        transfersErrorKey: null,
      });
    },
    setTransfersError(transfersErrorKey: string): void {
      patchState(store, { transfersErrorKey, transfersLoading: false });
    },
    /** Merge a polled page without rewinding statuses a WS event already advanced. */
    mergeTransfers(transfers: PrivateTransfer[]): void {
      const current = new Map(store.transfers().map(t => [t.id, t]));
      const merged = transfers.map(t => {
        const known = current.get(t.id);
        return known ? { ...t, status: advanceTransferStatus(known.status, t.status) } : t;
      });
      // The polled page *is* the list; the one row kept from outside it is the
      // tracked transfer — dropping it would blank the progress screen mid-poll.
      const trackedId = store.trackedTransferId();
      const tracked =
        trackedId === null || merged.some(t => t.id === trackedId)
          ? undefined
          : current.get(trackedId);
      patchState(store, { transfers: tracked ? [...merged, tracked] : merged });
    },
    upsertTransfer(transfer: PrivateTransfer): void {
      const known = store.transfers().find(t => t.id === transfer.id);
      const next = known
        ? { ...transfer, status: advanceTransferStatus(known.status, transfer.status) }
        : transfer;
      patchState(store, {
        transfers: known
          ? store.transfers().map(t => (t.id === transfer.id ? next : t))
          : [next, ...store.transfers()],
        trackedErrorKey: store.trackedTransferId() === transfer.id ? null : store.trackedErrorKey(),
      });
    },
    /** Optimistic move (WS event, or `FUNDING` right after signing); never backwards. */
    patchTransferStatus(transferId: string, status: PrivateTransferStatus): void {
      patchState(store, {
        transfers: store
          .transfers()
          .map(t =>
            t.id === transferId ? { ...t, status: advanceTransferStatus(t.status, status) } : t,
          ),
      });
    },
    setTracked(trackedTransferId: string | null): void {
      patchState(store, { trackedTransferId, trackedErrorKey: null });
    },
    setTrackedError(trackedErrorKey: string): void {
      patchState(store, { trackedErrorKey });
    },
    reset(): void {
      patchState(store, initialState);
    },
  })),
);
